/**
 * Shared Media Viewer
 * Used by: product-media-gallery, shoppable-videos
 */
(function() {
  window.MediaViewer = {
    /**
     * Initialize a media viewer
     * @param {Object} options
     * @param {HTMLElement} options.viewer - The viewer element
     * @param {Array} options.mediaItems - Array of media data objects
     * @param {number} options.startIndex - Starting index
     * @param {Function} options.onClose - Callback when viewer closes
     * @param {Function} options.onNavigate - Callback when navigating (for shoppable videos product updates)
     */
    open: function(options) {
      var viewer = options.viewer;
      var mediaItems = options.mediaItems || [];
      var currentIndex = options.startIndex || 0;
      var onClose = options.onClose;
      var onNavigate = options.onNavigate;
      
      if (!viewer || mediaItems.length === 0) return;
      
      var isMuted = true;
      
      // Elements
      var overlay = viewer.querySelector('.media-viewer__overlay');
      var content = viewer.querySelector('.media-viewer__content');
      var counter = viewer.querySelector('[data-viewer-counter]');
      var progressBar = viewer.querySelector('[data-viewer-progress]');
      var muteBtn = viewer.querySelector('[data-viewer-mute]');
      var closeBtn = viewer.querySelectorAll('[data-media-viewer-close]');
      var prevBtn = viewer.querySelector('[data-viewer-prev]');
      var nextBtn = viewer.querySelector('[data-viewer-next]');
      var videoEl = viewer.querySelector('[data-viewer-video]');
      var imageEl = viewer.querySelector('[data-viewer-image]');
      
      function showMedia(index) {
        var item = mediaItems[index];
        if (!item) return;
        
        currentIndex = index;
        
        // Update counter
        if (counter) {
          counter.textContent = (index + 1) + '/' + mediaItems.length;
        }
        
        // Update navigation buttons
        if (prevBtn) prevBtn.disabled = index === 0;
        if (nextBtn) nextBtn.disabled = index === mediaItems.length - 1;
        
        // Reset progress
        if (progressBar) {
          progressBar.style.transition = 'none';
          progressBar.style.transform = 'scaleX(0)';
        }
        
        // Stop any current video
        stopVideo();
        
        var type = item.type || 'image';
        
        // Set media type on viewer for CSS
        viewer.setAttribute('data-media-type', type);
        
        if (type === 'video' || type === 'external_video') {
          // Show video
          if (item.videoSrc) {
            videoEl.src = item.videoSrc;
            videoEl.poster = item.poster || '';
            videoEl.muted = isMuted;
            videoEl.loop = true;
            
            // Smooth progress animation + video fit detection
            videoEl.onloadedmetadata = function() {
              if (videoEl.duration && progressBar) {
                progressBar.style.transition = 'none';
                progressBar.style.transform = 'scaleX(0)';
                void progressBar.offsetWidth;
                progressBar.style.transition = 'transform ' + videoEl.duration + 's linear';
                progressBar.style.transform = 'scaleX(1)';
              }
              
              // Handle media fit based on setting
              var mediaFit = viewer.getAttribute('data-media-fit') || 'auto';
              if (mediaFit === 'auto') {
                // Auto-detect: portrait media uses cover, square/landscape uses contain
                var aspectRatio = videoEl.videoWidth / videoEl.videoHeight;
                if (aspectRatio < 0.8) {
                  // Portrait (taller than wide) - use cover
                  videoEl.style.objectFit = 'cover';
                } else {
                  // Square or landscape - use contain to show full media
                  videoEl.style.objectFit = 'contain';
                }
              } else {
                videoEl.style.objectFit = mediaFit;
              }
            };
            
            videoEl.onended = function() {
              if (videoEl.loop && progressBar && videoEl.duration) {
                progressBar.style.transition = 'none';
                progressBar.style.transform = 'scaleX(0)';
                void progressBar.offsetWidth;
                progressBar.style.transition = 'transform ' + videoEl.duration + 's linear';
                progressBar.style.transform = 'scaleX(1)';
              }
            };
            
            videoEl.play().catch(function() {});
          }
          
          // Show mute button
          if (muteBtn) muteBtn.style.display = 'flex';
          if (imageEl) imageEl.style.display = 'none';
          if (videoEl) videoEl.style.display = 'block';
        } else {
          // Show image
          if (item.imageSrc) {
            imageEl.src = item.imageSrc;
            imageEl.alt = item.alt || '';
            
            // Handle media fit based on setting for images
            imageEl.onload = function() {
              var mediaFit = viewer.getAttribute('data-media-fit') || 'auto';
              if (mediaFit === 'auto') {
                // Auto-detect: portrait images use cover, square/landscape uses contain
                var aspectRatio = imageEl.naturalWidth / imageEl.naturalHeight;
                if (aspectRatio < 0.8) {
                  // Portrait (taller than wide) - use cover
                  imageEl.style.objectFit = 'cover';
                } else {
                  // Square or landscape - use contain to show full image
                  imageEl.style.objectFit = 'contain';
                }
              } else {
                imageEl.style.objectFit = mediaFit;
              }
            };
          }
          
          // Hide mute button
          if (muteBtn) muteBtn.style.display = 'none';
          if (videoEl) videoEl.style.display = 'none';
          if (imageEl) imageEl.style.display = 'block';
          
          // Full progress for images
          if (progressBar) {
            progressBar.style.transition = 'none';
            progressBar.style.transform = 'scaleX(1)';
          }
        }
        
        // Callback for external updates (e.g., product card in shoppable videos)
        if (onNavigate) onNavigate(index, item);
      }
      
      function stopVideo() {
        if (videoEl) {
          videoEl.pause();
          videoEl.onloadedmetadata = null;
          videoEl.onended = null;
          videoEl.src = '';
        }
      }
      
      function closeViewer() {
        stopVideo();
        viewer.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('media-viewer-open');
        
        if (progressBar) {
          progressBar.style.transition = 'none';
          progressBar.style.transform = 'scaleX(0)';
        }
        
        // Remove event listeners
        cleanup();
        
        if (onClose) onClose();
      }
      
      function toggleMute() {
        isMuted = !isMuted;
        if (videoEl) videoEl.muted = isMuted;
        if (muteBtn) muteBtn.classList.toggle('is-muted', isMuted);
      }
      
      function goToPrev() {
        if (currentIndex > 0) {
          showMedia(currentIndex - 1);
        }
      }
      
      function goToNext() {
        if (currentIndex < mediaItems.length - 1) {
          showMedia(currentIndex + 1);
        }
      }
      
      function handleKeydown(e) {
        if (viewer.getAttribute('aria-hidden') === 'false') {
          if (e.key === 'Escape') closeViewer();
          else if (e.key === 'ArrowLeft') goToPrev();
          else if (e.key === 'ArrowRight') goToNext();
        }
      }
      
      // Swipe support
      var touchStartX = 0;
      var touchStartY = 0;
      
      function handleTouchStart(e) {
        touchStartX = e.changedTouches[0].screenX;
        touchStartY = e.changedTouches[0].screenY;
      }
      
      function handleTouchEnd(e) {
        var diffX = touchStartX - e.changedTouches[0].screenX;
        var diffY = touchStartY - e.changedTouches[0].screenY;
        
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
          if (diffX > 0) goToNext();
          else goToPrev();
        }
      }
      
      function handleCloseClick() {
        closeViewer();
      }
      
      // Attach event listeners
      closeBtn.forEach(function(btn) {
        btn.addEventListener('click', handleCloseClick);
      });
      
      if (muteBtn) muteBtn.addEventListener('click', toggleMute);
      if (prevBtn) prevBtn.addEventListener('click', goToPrev);
      if (nextBtn) nextBtn.addEventListener('click', goToNext);
      
      document.addEventListener('keydown', handleKeydown);
      viewer.addEventListener('touchstart', handleTouchStart, { passive: true });
      viewer.addEventListener('touchend', handleTouchEnd, { passive: true });
      
      function cleanup() {
        closeBtn.forEach(function(btn) {
          btn.removeEventListener('click', handleCloseClick);
        });
        if (muteBtn) muteBtn.removeEventListener('click', toggleMute);
        if (prevBtn) prevBtn.removeEventListener('click', goToPrev);
        if (nextBtn) nextBtn.removeEventListener('click', goToNext);
        document.removeEventListener('keydown', handleKeydown);
        viewer.removeEventListener('touchstart', handleTouchStart);
        viewer.removeEventListener('touchend', handleTouchEnd);
      }
      
      // Show viewer
      viewer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('media-viewer-open');
      
      // Set initial mute state
      if (muteBtn) muteBtn.classList.toggle('is-muted', isMuted);
      
      // Show initial media
      showMedia(currentIndex);
      
      // Return controls for external use
      return {
        close: closeViewer,
        goTo: showMedia,
        next: goToNext,
        prev: goToPrev
      };
    }
  };
})();
